import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, EditOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useRef, useState } from "react"

import { PermissionActionType } from "@/constants/permissions"
import { PriorityStatus } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import type { PreferenceAssociationItem } from "@/screens/CustomerProducts/hooks/useProductPreferenceAssociationList"

interface AssociationLocationRowActionsProps {
  row: PreferenceAssociationItem
  onEditOpen: (row: PreferenceAssociationItem) => void
  onRemoveOpen: (row: PreferenceAssociationItem) => void
}

export const AssociationLocationRowActions: FC<AssociationLocationRowActionsProps> = ({
  row,
  onEditOpen,
  onRemoveOpen,
}) => {
  const { hasPermissions } = usePermissionManager()
  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  if (!row.shipToLocationId || !canEdit) return null

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleEditClick = () => {
    handleMenuClose()
    onEditOpen(row)
  }

  const handleRemoveClick = () => {
    handleMenuClose()
    onRemoveOpen(row)
  }

  return (
    <Box ref={anchorElRef}>
      <IconButton size='small' onClick={handleMenuOpen} data-testid='association-location-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='association-location-row-actions-menu'
      >
        <MenuItem onClick={handleEditClick} data-testid='association-location-edit-menu-item'>
          <ListItemIcon>
            <EditOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
          </ListItemIcon>
          <ListItemText primary='Edit Association' />
        </MenuItem>
        {row.priorityStatus !== PriorityStatus.DoNotUse && (
          <MenuItem onClick={handleRemoveClick} data-testid='association-location-remove-menu-item'>
            <ListItemIcon>
              <DeleteOutlined sx={{ fontVariationSettings: '"wght" 300' }} />
            </ListItemIcon>
            <ListItemText primary='Remove Association' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
